import { createAction } from 'redux-actions';
import sizeof from 'object-sizeof';

import {
  clearElementWithIdsFromCache,
  clearSectionContentWithIdsFromCache,
  clearOCRSectionContentWithIdsFromCache,
  clearOCRSectionImageWithIdsFromCache,
} from 'store/actions/action-creators';
import {
  getRevisionsSectionsRequest,
  getRevisionsSectionsOCRRequest,
} from 'api/sections-api';
import { CONTEXT_KEY } from 'api/api-constants';

export const clearCurrentBlacklineContentWithIdsFromCache = createAction(
  'CLEAR_CURRENT_BLACKLINE_VIEW_CHANGES',
);
export const createSectionCacheTracker = createAction(
  'CACHE_CREATE_SECTION_TRACKER',
);

export const createLeftSectionCacheTracker = createAction(
  'CACHE_LEFT_CREATE_SECTION_TRACKER',
);

export const updateSectionViewedTimestamp = createAction(
  'CACHE_UPDATE_SECTION_VIEWED_TIMESTAMP',
);

export const addSectionToCache = createAction('ADD_SECTION_TO_CACHE');
export const addLeftSectionToCache = createAction('ADD_LEFT_SECTION_TO_CACHE');

export const updatePurgedCacheTracker = createAction(
  'UPDATE_PURGED_CACHE_TRACKER',
);

export const contentSectionMapLoading = createAction(
  'CONTENT_SECTION_MAP_LOADING',
);
export const contentSectionMapLoaded = createAction(
  'CONTENT_SECTION_MAP_LOADED',
);
export const contentSectionMapError = createAction('CONTENT_SECTION_MAP_ERROR');

export const leftContentSectionMapLoading = createAction(
  'LEFT_CONTENT_SECTION_MAP_LOADING',
);
export const leftContentSectionMapLoaded = createAction(
  'LEFT_CONTENT_SECTION_MAP_LOADED',
);
export const leftContentSectionMapError = createAction(
  'LEFT_CONTENT_SECTION_MAP_ERROR',
);

export const checkCacheToPurge = () => async (dispatch, getStore) => {
  const store = getStore();
  const { statementContent, selectedStatement } = store.data;
  const { tracker } = statementContent.sectionsCache;
  const {
    sectionsInView,
    modes: { blacklineViewMode },
  } = store.ui.statementPage;

  if (tracker.cacheOverThreshold()) {
    const {
      newViewedSectionMetricsMap,
      newViewedSectionQueue,
      reducedTotalSize,
      removedSectionIds,
    } = tracker.getPurgedCache({ sectionsInView });
    dispatch(
      updatePurgedCacheTracker({
        viewedSectionMetricsMap: newViewedSectionMetricsMap,
        viewedSectionQueue: newViewedSectionQueue,
        totalCacheMemorySize: reducedTotalSize,
      }),
    );

    if (selectedStatement.isOCR) {
      dispatch(clearOCRSectionContentWithIdsFromCache({ removedSectionIds }));
      dispatch(clearOCRSectionImageWithIdsFromCache({ removedSectionIds }));
      dispatch(clearElementWithIdsFromCache({ removedSectionIds }));
      blacklineViewMode &&
        dispatch(
          clearCurrentBlacklineContentWithIdsFromCache({ removedSectionIds }),
        );
    } else {
      dispatch(clearSectionContentWithIdsFromCache({ removedSectionIds }));
    }
  }
};

export const calculateSizeAndAddSectionToCache =
  ({ sectionId }) =>
  async (dispatch, getStore) => {
    const { cache } = getStore().data.statementContent.sectionsCache;
    const sectionMemorySize = sizeof(cache.get(sectionId));

    dispatch(
      addSectionToCache({
        sectionId,
        sectionMemorySize,
      }),
    );
  };

export const getContentSectionMapRequest =
  ({ revisionId, statementId }) =>
  async (dispatch, getStore) => {
    dispatch(contentSectionMapLoading());
    try {
      await getRevisionsSectionsRequest({
        statementId,
        revisionId,
        onlyBookmarkSection: false,
        asTreeList: false,
      }).then((response) => {
        dispatch(contentSectionMapLoaded({ response }));
      });
    } catch (error) {
      dispatch(contentSectionMapError(error));
    }
  };

export const getContentSectionMapOCRRequest =
  ({ revisionId, isOCR, statementId }) =>
  async (dispatch, getStore) => {
    dispatch(contentSectionMapLoading());
    try {
      await getRevisionsSectionsOCRRequest({
        revisionId,
        statementId,
        onlyBookmarkSection: false,
        asTreeList: false,
      }).then((response) => {
        dispatch(contentSectionMapLoaded({ response, isOCR }));
      });
    } catch (error) {
      dispatch(contentSectionMapError(error));
    }
  };

export const getLeftContentSectionMapOCRRequest =
  ({ revisionId, isOCR }) =>
  async (dispatch, getStore) => {
    dispatch(leftContentSectionMapLoading());
    try {
      await getRevisionsSectionsOCRRequest({
        revisionId,
        onlyBookmarkSection: false,
        asTreeList: false,
      }).then((response) => {
        dispatch(leftContentSectionMapLoaded({ response, isOCR }));
      });
    } catch (error) {
      dispatch(leftContentSectionMapError(error));
    }
  };

export const getLeftContentSectionMapRequest =
  ({ statementId, revisionId, projectId }) =>
  async (dispatch, getStore) => {
    dispatch(leftContentSectionMapLoading());
    try {
      await getRevisionsSectionsRequest({
        statementId,
        revisionId,
        onlyBookmarkSection: false,
        asTreeList: false,
        contextKeyForApiCall: CONTEXT_KEY.PROJECT_CONTEXT,
        contextValue: projectId,
      }).then((response) => {
        dispatch(leftContentSectionMapLoaded({ response }));
      });
    } catch (error) {
      dispatch(leftContentSectionMapError(error));
    }
  };
